import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { singleSettingSelector } from "../../../redux/setting";
import { observer } from "../../../utils/observer";

const initialState = {
  update: (state) => void 0,
  canSave: false,
  autoSaveEnabled: false,
  baselineEnabled: false,
  simulationEnabled: false,
  users: [],
  api: {
    setGantt(g) {},
    setHistogram(g) {},
    get gantt() {
      return null;
    },
    get histogram() {
      return null;
    },
    get activeElement() {
      return null;
    },
  },
};

const PageApiContext = createContext(initialState);

export function usePageApi() {
  return useContext(PageApiContext);
}

export function withPageApi(Component) {
  return (props) => {
    return <PageApiProvider>{(pageApi, api) => <Component {...props} {...pageApi} api={api} />}</PageApiProvider>;
  };
}

function PageApiProvider({ children }) {
  const configs = useSelector(singleSettingSelector);

  const [state, setState] = useState({
    ...initialState,
    autoSaveEnabled: configs.autoSave || false,
  });

  const api = useMemo(() => {
    let _gantt = null;
    let _histogram = null;
    const api = {
      setGantt(g) {
        _gantt = g;
      },
      get gantt() {
        return _gantt?.instance;
      },
      setHistogram(g) {
        _histogram = g;
      },
      get histogram() {
        return _histogram;
      },
      get activeElement() {
        if (!api.gantt?.hidden) {
          return api.gantt;
        }
        if (!api.histogram?.hidden) {
          return api.histogram;
        }
        return null;
      },
    };
    return api;
  }, []);
  const update = useCallback((newState) => {
    setState((state) => {
      return {
        ...state,
        ...newState,
      };
    });
  }, []);
  const value = useMemo(() => {
    return {
      ...state,
      update,
      api,
    };
  }, [state, update, api]);
  useEffect(() => {
    const unsubscribeConflict = observer.subscribe("conflictResolved", () => {
      if (api?.gantt?.project?.changes) {
        update({
          canSave: true,
        });
      }
    });
    return () => {
      unsubscribeConflict();
    };
  }, [api, update]);
  return <PageApiContext.Provider value={value}>{children(value, api)}</PageApiContext.Provider>;
}
